import { apiClient } from '@shared/utils/api-client';

/** Параметры запроса списка доставок */
export interface GetDeliveriesParams {
  search_text?: string | null;
  status?: string | null;
  offset?: number;
  count?: number;
  next_page_id?: number | null;
  delivery_ids?: number[];
}

/** Курьер в ответе доставки */
export interface DostavistaDeliveryCourier {
  courier_id?: number;
  surname?: string | null;
  name?: string | null;
  middlename?: string | null;
  phone?: string | null;
  photo_url?: string | null;
  latitude?: string | null;
  longitude?: string | null;
}

/** Точка в заказе (point_id, delivery_id, адрес, время прибытия) */
export interface DostavistaOrderPoint {
  point_type?: string | null;
  point_id?: number | null;
  delivery_id?: number | null;
  client_order_id?: string | null;
  address?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  required_start_datetime?: string | null;
  required_finish_datetime?: string | null;
  arrival_start_datetime?: string | null;
  arrival_finish_datetime?: string | null;
  estimated_arrival_datetime?: string | null;
  courier_visit_datetime?: string | null;
  [key: string]: unknown;
}

/** Заказ Достависты со списком точек */
export interface DostavistaOrderListItem {
  order_id: number;
  order_name?: string | null;
  type?: string | null;
  matter?: string | null;
  status?: string | null;
  status_description?: string | null;
  payment_amount?: string | null;
  delivery_fee_amount?: string | null;
  /** Штраф за позднюю отмену этого заказа */
  late_cancellation_penalty_amount?: string | null;
  created_datetime?: string | null;
  finish_datetime?: string | null;
  points?: DostavistaOrderPoint[];
  [key: string]: unknown;
}

/** Одна доставка из GET /deliveries (с заказом и точками при ответе для админки) */
export interface DostavistaDelivery {
  delivery_id: number;
  delivery_type?: string | null;
  order_id?: number | null;
  client_id?: number;
  client_order_id?: string | null;
  address?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  status?: string | null;
  status_datetime?: string | null;
  created_datetime?: string | null;
  order_name?: string | null;
  order_payment_amount?: string | null;
  delivery_price_amount?: string | null;
  point_id?: number | null;
  contact_person?: {
    phone?: string | null;
    phone_extension?: string | null;
    name?: string | null;
  } | null;
  note?: string | null;
  required_start_datetime?: string | null;
  required_finish_datetime?: string | null;
  /** Ожидаемое время прибытия на точку */
  estimated_arrival_datetime?: string | null;
  /** Фактическое время прибытия на адрес */
  courier_visit_datetime?: string | null;
  taking_amount?: string | null;
  buyout_amount?: string | null;
  cancel_reason?: string | null;
  courier?: DostavistaDeliveryCourier | null;
  /** Заказ Достависты со списком точек (при ответе для админки) */
  dostavistaOrder?: DostavistaOrderListItem | null;
  /** Наш заказ из БД */
  order?: { id: number; orderNumber: string } | null;
  /** Точка самовывоза из БД */
  pickupPoint?: {
    id: number;
    city: string;
    address: string;
    metro: string | null;
    phoneNumber: string | null;
  } | null;
  [key: string]: unknown;
}

export interface GetDeliveriesResponse {
  deliveries: DostavistaDelivery[];
  deliveries_count: number;
  next_page_id: number | null;
}

/** Ответ отмены заказа в Достависте */
export interface CancelOrderResponse {
  is_successful: boolean;
  order?: DostavistaOrderListItem;
  errors?: string[];
}

/** Ответ редактирования заказа в Достависте (edit-order) */
export interface OrderReturnResponse {
  is_successful: boolean;
  order?: unknown;
  errors?: string[];
  paymentAmountBeforeRub?: number;
  paymentAmountAfterRub?: number;
}

export const deliveryApi = {
  /** Отменить заказ доставки в Достависте по ID нашего заказа из БД */
  cancelOrder: async (orderId: number): Promise<CancelOrderResponse> => {
    return apiClient.post<CancelOrderResponse>('/delivery/cancel-order', { orderId });
  },

  /** Заказать возврат: включить возврат на первую точку. Принимает order_id заказа в Достависте */
  orderReturn: async (dostavistaOrderId: number): Promise<OrderReturnResponse> => {
    return apiClient.post<OrderReturnResponse>('/delivery/order-return', {
      dostavistaOrderId,
    });
  },

  getDeliveries: async (
    params?: GetDeliveriesParams,
  ): Promise<GetDeliveriesResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.search_text) searchParams.set('search_text', params.search_text);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.offset != null) searchParams.set('offset', String(params.offset));
    if (params?.count != null) searchParams.set('count', String(params.count));
    if (params?.next_page_id != null)
      searchParams.set('next_page_id', String(params.next_page_id));
    if (params?.delivery_ids?.length)
      params.delivery_ids.forEach((id) => searchParams.append('delivery_ids', String(id)));
    const query = searchParams.toString();
    const url = query ? `/delivery/deliveries?${query}` : '/delivery/deliveries';
    return apiClient.get<GetDeliveriesResponse>(url);
  },
};
